import { createSlice } from '@reduxjs/toolkit';
import {
    onAddInventoryButtonDisable,
    setOpenedInventoryGuid,
    setIsCopyMode,
} from './inventorySlice';
import { setNeedUpdateTotalInfo } from './totalInfoSlice';
import {
    getWarehouseTotalInfoRequest,
    addNewWarehouseTotalInfo,
    removeWarehouseTotalInfo,
} from './warehouseTotalInfoSlice';
import {cancelMoveInventory} from "./moveInventoryQuantityModalSlice";

export const boardSlice = createSlice({
    name: 'board',
    initialState: [],
    reducers: {
        loadEquipmentData: (state, action) => action.payload.data.response.equipment,
        moveInventoryToWarehouse: (state, action) => {
            const { inventoryPosition, inventory, warehouseId } = action.payload;
            const warehouse = state.find((item) => item.id === warehouseId);
            warehouse.items.splice(inventoryPosition, 0, inventory);
            warehouse.items = warehouse.items.map((item, index) => ({
                ...item,
                // eslint-disable-next-line camelcase
                sort_index: index,
            }));
        },
        removeInventoryFromWarehouse: (state, action) => {
            const { inventoryPosition, warehouseId } = action.payload;
            const warehouse = state.find((item) => item.id === warehouseId);
            warehouse.items.splice(inventoryPosition, 1);
            warehouse.items.forEach((item, index) => {
                // eslint-disable-next-line camelcase
                item.sort_index = index;
            });
        },
        reOrderInventoryPosition: (state, action) => {
            const { oldIndex, newIndex, warehouseId } = action.payload;
            const warehouse = state.find((item) => item.id === warehouseId);
            const [inventory] = warehouse.items.splice(oldIndex, 1);
            warehouse.items.splice(newIndex, 0, inventory);
            warehouse.items = warehouse.items.map((item, index) => ({
                ...item,
                // eslint-disable-next-line camelcase
                sort_index: index,
            }));
            queueable.addTask({
                data: {
                    guid: inventory.guid,
                    toWarehouseId: warehouseId,
                    newIndex,
                },
                type: 'sort-inventory',
            });
        },
        reOrderWarehousePosition: (state, action) => {
            const { oldIndex, newIndex } = action.payload;
            const [warehouse] = state.splice(oldIndex, 1);
            state.splice(newIndex, 0, warehouse);
            state.forEach((item, index) => {
                // eslint-disable-next-line camelcase
                item.sort_index = index;
            });
            queueable.addTask({
                data: {
                    warehouseId: warehouse.id,
                    newIndex,
                },
                type: 'sort-warehouse',
            });
        },
        addNewWarehouse: (state, action) => {
            state.push(action.payload);
            state.forEach((item, index) => {
                // eslint-disable-next-line camelcase
                item.sort_index = index;
            });
        },
        deleteWarehouse: (state, action) => {
            const currentWarehouseIndex = state.findIndex((item) => item.id === action.payload);
            state.splice(currentWarehouseIndex, 1);
            state.forEach((item, index) => {
                // eslint-disable-next-line camelcase
                item.sort_index = index;
            });
        },
        renameWarehouse: (state, action) => {
            const currentWarehouse = state.find((item) => item.id === action.payload.data.id);
            currentWarehouse.title = action.payload.newWarehouseTitle;
        },
        addInventoryToWarehouse: (state, action) => {
            const { id, newData } = action.payload;
            const currentWarehouseIndex = state.findIndex((item) => item.id === id);
            state[currentWarehouseIndex].items.splice(newData.sort_index, 0, newData);
        },
        copyInventoryToWarehouse: (state, action) => {
            const { id, newData } = action.payload;
            const currentWarehouseIndex = state.findIndex((item) => item.id === id);
            state[currentWarehouseIndex].items.splice(newData.sort_index, 0, newData);
        },
        moveInventory: (state, action) => {
            const { fromWarehouseId, toWarehouseId, inventoryGuid } = action.payload;
            const fromWarehouse = state.find((item) => item.id === fromWarehouseId);
            const toWarehouse = state.find((item) => item.id === toWarehouseId);
            const inventoryIndex = fromWarehouse.items.findIndex(
                (item) => item.guid === inventoryGuid
            );
            const [inventory] = fromWarehouse.items.splice(inventoryIndex, 1);
            toWarehouse.items.push(inventory);
            fromWarehouse.items.forEach((item, index) => {
                // eslint-disable-next-line camelcase
                item.sort_index = index;
            });
            toWarehouse.items = toWarehouse.items.map((item, index) => ({
                ...item,
                // eslint-disable-next-line camelcase
                sort_index: index,
            }));
        },
        moveAllInventory: (state, action) => {
            const { fromWarehouseId, toWarehouseId } = action.payload;
            const fromWarehouse = state.find((item) => item.id === fromWarehouseId);
            const toWarehouse = state.find((item) => item.id === toWarehouseId);
            toWarehouse.items = toWarehouse.items.concat(fromWarehouse.items);
            fromWarehouse.items.splice(0, fromWarehouse.items.length);
            toWarehouse.items.forEach((inventory, index) => {
                // eslint-disable-next-line camelcase
                inventory.sort_index = index;
            });
        },
        moveFilteredInventories: (state, action) => {
            const { fromWarehouseId, toWarehouseId, filteredInventories } = action.payload;
            const toWarehouse = state.find((item) => item.id === toWarehouseId);
            toWarehouse.items = toWarehouse.items.concat(filteredInventories);
            const filteredInventoriesGuids = filteredInventories.map((item) => item.guid);
            const fromWarehouse = state.find((item) => item.id === fromWarehouseId);
            fromWarehouse.items = fromWarehouse.items.filter(
                (item) => !filteredInventoriesGuids.includes(item.guid)
            );
        },
        changeInventoryStatus: (state, action) => {
            const { warehouseId, inventoryGuid, status } = action.payload;
            const warehouse = state.find((item) => item.id === warehouseId);
            const inventory = warehouse.items.find((item) => item.guid === inventoryGuid);
            inventory.status = status;
            // eslint-disable-next-line camelcase
            inventory.free_date = null;
        },
        renameInventory: (state, action) => {
            const { warehouseId, inventoryGuid, title } = action.payload;
            const warehouse = state.find((item) => item.id === warehouseId);
            const inventory = warehouse.items.find((item) => item.guid === inventoryGuid);
            inventory.title = title;
        },
        updateInventoryAmount: (state, action) => {
            const { warehouseId, inventoryGuid, amount } = action.payload;
            const warehouse = state.find((item) => item.id === warehouseId);
            const inventory = warehouse.items.find((item) => item.guid === inventoryGuid);
            inventory.amount = amount;
        },
        updateFreeDate: (state, action) => {
            const { warehouseId, inventoryGuid, freeDate } = action.payload;
            const warehouse = state.find((item) => item.id === warehouseId);
            const inventory = warehouse.items.find((item) => item.guid === inventoryGuid);
            // eslint-disable-next-line camelcase
            inventory.free_date = freeDate;
        },
        updateInventoryPhoto: (state, action) => {
            const { warehouseId, inventoryGuid, preview } = action.payload;
            const warehouse = state.find((item) => item.id === warehouseId);
            const inventory = warehouse.items.find((item) => item.guid === inventoryGuid);
            // eslint-disable-next-line camelcase
            inventory.preview_url = preview;
        },
    },
});

export const {
    removeInventoryFromWarehouse,
    reOrderInventoryPosition,
    reOrderWarehousePosition,
    addNewWarehouse,
    loadEquipmentData,
    deleteWarehouse,
    renameWarehouse,
    addInventoryToWarehouse,
    moveInventory,
    moveAllInventory,
    renameInventory,
    updateInventoryAmount,
    updateFreeDate,
    updateInventoryPhoto,
    moveFilteredInventories,
    copyInventoryToWarehouse,
} = boardSlice.actions;

export const sendAddWarehouseRequest = (payload) => (dispatch) => {
    let body = '';
    if (payload.id) {
        // eslint-disable-next-line camelcase
        body = { project_id: payload.id };
    } else {
        body = { title: payload.title };
    }
    const bodyFetch = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json;charset=utf-8',
        },
        body: JSON.stringify(body),
    };

    fetch(routable.getRoute('createWarehouse'), bodyFetch)
        .then((response) => response.json())
        .then((data) => {
            dispatch(addNewWarehouse(data.response));
            dispatch(
                addNewWarehouseTotalInfo({
                    warehouseId: data.response.id,
                    totalInfo: data.response.total_info,
                })
            );
        })
        .catch((error) => {
            console.error(error.message);
        });
};

export const sendDeleteWarehouseRequest = (payload) => (dispatch) => {
    dispatch(deleteWarehouse(payload));
    const url = helpable.replaceUrlParameters(
        routable.getRoute('deleteWarehouse'),
        // eslint-disable-next-line camelcase
        { _warehouse_id_: payload }
    );
    fetch(url, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json;charset=utf-8',
        },
    })
        .then((response) => response.json())
        .then(() => {
            dispatch(removeWarehouseTotalInfo(payload));
        });
};

export const sendRenameWarehouseRequest = (payload) => (dispatch) => {
    dispatch(renameWarehouse(payload));

    const url = helpable.replaceUrlParameters(
        routable.getRoute('renameWarehouse'),
        // eslint-disable-next-line camelcase
        { _warehouse_id_: payload.data.id }
    );
    fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json;charset=utf-8',
        },
        body: JSON.stringify({ title: payload.newWarehouseTitle }),
    });
};

export const sendCreateInventoryRequest = (payload) => (dispatch) => {
    dispatch(onAddInventoryButtonDisable(true));
    const url = helpable.replaceUrlParameters(routable.getRoute('createTool'), {
        // eslint-disable-next-line camelcase
        _warehouse_id_: payload,
    });

    fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json;charset=utf-8',
        },
    })
        .then((response) => response.json())
        .then((data) => {
            dispatch(addInventoryToWarehouse({ id: payload, newData: data.response }));
            dispatch(onAddInventoryButtonDisable(false));
            dispatch(setOpenedInventoryGuid(data.response.guid));
            dispatch(setNeedUpdateTotalInfo(true));
            dispatch(getWarehouseTotalInfoRequest(payload));
        });
};
export const sendCopyInventoryRequest = (payload) => (dispatch) => {
    const { warehouseId, inventoryGuid } = payload;
    dispatch(onAddInventoryButtonDisable(true));
    const url = helpable.replaceUrlParameters(routable.getRoute('copyInventory'), {
        // eslint-disable-next-line camelcase
        _guid_: inventoryGuid,
    });

    fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json;charset=utf-8',
        },
    })
        .then((response) => response.json())
        .then((data) => {
            dispatch(setIsCopyMode(true));
            dispatch(copyInventoryToWarehouse({ id: warehouseId, newData: data.response }));
            dispatch(onAddInventoryButtonDisable(false));
            dispatch(setOpenedInventoryGuid(data.response.guid));
            dispatch(setNeedUpdateTotalInfo(true));
        });
};

export const sendMoveInventoryRequest = (payload) => (dispatch) => {
    dispatch(moveInventory(payload));
    queueable.addTask({
        data: {
            guid: payload.inventoryGuid,
            toWarehouseId: payload.toWarehouseId,
            newIndex: payload.newIndex,
        },
        type: 'sort-inventory',
    });
};

export const sendMoveInventoryToWarehouseFullyRequest = (payload) => (dispatch) => {
    queueable.addTask({
        data: {
            guid: payload.inventory.guid,
            toWarehouseId: payload.warehouseId,
            newIndex: payload.inventoryPosition,
        },
        type: 'sort-inventory',
    });

    dispatch(boardSlice.actions.moveInventoryToWarehouse(payload));
    // TODO rewrite the logic and remove settimeout
    setTimeout(() => {
        if (typeof payload.fromWarehouseId !== 'undefined') {
            dispatch(getWarehouseTotalInfoRequest(payload.fromWarehouseId));
        }
        dispatch(getWarehouseTotalInfoRequest(payload.warehouseId));
    }, 1000);
};

export const sendMoveInventoryToWarehousePartiallyRequest = (payload) => (dispatch) => {
    const { warehouseToId, warehouseFromId,  inventoryGuid, inventoryPosition, quantity } = payload;
    const url = helpable.replaceUrlParameters(routable.getRoute('partiallyMoveInventory'), {
        _guid_: inventoryGuid,
    });

    fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json;charset=utf-8',
        },
        body: JSON.stringify({
            toWarehouseId: warehouseToId,
            sortIndex: inventoryPosition,
            quantity,
        })
    })
        .then((response) => response.json())
        .then((data) => {
            dispatch(boardSlice.actions.removeInventoryFromWarehouse({
                inventoryPosition,
                warehouseId: warehouseToId,
            }));
            dispatch(copyInventoryToWarehouse({ id: warehouseToId, newData: data.response }));
            dispatch(getWarehouseTotalInfoRequest(warehouseToId));
            dispatch(getWarehouseTotalInfoRequest(warehouseFromId));
            dispatch(setNeedUpdateTotalInfo(true));
            dispatch(cancelMoveInventory(false));
        });
};

export const sendMoveAllInventoryRequest = (payload) => (dispatch) => {
    dispatch(moveAllInventory(payload));
    queueable.addTask({
        data: {
            fromWarehouseId: payload.fromWarehouseId,
            toWarehouseId: payload.toWarehouseId,
        },
        type: 'move-all-inventories',
    });
    // TODO rewrite the logic and remove settimeout
    setTimeout(() => {
        dispatch(getWarehouseTotalInfoRequest(payload.toWarehouseId));
        dispatch(getWarehouseTotalInfoRequest(payload.fromWarehouseId));
    }, 1000);
};

export const sendMoveFilteredInventoriesRequest = (payload) => (dispatch) => {
    dispatch(moveFilteredInventories(payload));
    const inventoriesGuidList = payload.filteredInventories.map((item) => item.guid);
    queueable.addTask({
        data: {
            inventoriesGuidList,
            toWarehouseId: payload.toWarehouseId,
        },
        type: 'move-part-of-inventories',
    });
};

export const sendDeleteInventoryRequest = (payload) => (dispatch) => {
    dispatch(
        removeInventoryFromWarehouse({
            inventoryPosition: payload.inventoryPosition,
            warehouseId: payload.warehouseId,
        })
    );
    const url = helpable.replaceUrlParameters(routable.getRoute('removeTool'), {
        _guid_: payload.inventoryGuid,
    });
    fetch(url, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json;charset=utf-8',
        },
    })
        .then((response) => response.json())
        .then(() => {
            dispatch(setNeedUpdateTotalInfo(true));
            dispatch(getWarehouseTotalInfoRequest(payload.warehouseId));
        });
};

export const sendChangeInventoryStatusRequest = (payload) => (dispatch) => {
    dispatch(boardSlice.actions.changeInventoryStatus(payload));
    const url = helpable.replaceUrlParameters(routable.getRoute('updateToolStatus'), {
        _guid_: payload.inventoryGuid,
    });

    return fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json;charset=utf-8',
        },
        body: JSON.stringify({ status: payload.status }),
    });
};

export const loadWarehouseListRequest = () =>
    fetch(routable.getRoute('warehouseList'), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json;charset=utf-8',
        },
    });

export const updateInventoryRequest = (payload, callback) => (dispatch) => {
    const { body, inventoryGuid, warehouseId } = payload;
    const url = helpable.replaceUrlParameters(routable.getRoute('updateInventory'), {
        _guid_: inventoryGuid,
    });

    fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json;charset=utf-8',
        },
        body,
    })
        .then((response) => response.json())
        .then((data) => {
            dispatch(setNeedUpdateTotalInfo(true));
            if (warehouseId) {
                dispatch(getWarehouseTotalInfoRequest(warehouseId));
            }
            if (typeof callback === 'function') {
                callback(data);
            }
        });
};

export default boardSlice.reducer;
